import { useState, useEffect } from 'react';
import { PageHeader, Badge, Sparkline, cx, type ToneType } from './shared';
import { Icon } from './ui/icons';

interface TimelineEvent {
  id: string;
  timestamp: string;
  type: 'threat_detected' | 'vulnerability_found' | 'incident_resolved' | 'policy_violation' | 'access_anomaly' | 'compliance_check';
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  title: string;
  description: string;
  source: string;
  affected_assets: string[];
  duration?: number; // in minutes
  status: 'ongoing' | 'resolved' | 'investigating' | 'mitigated';
  tags: string[];
  location?: string;
  user?: string;
  impact_score: number; // 1-10
}

interface TimelineMetrics {
  period: string;
  total_events: number;
  events_by_hour: number[];
  events_by_severity: Record<string, number>;
  events_by_type: Record<string, number>;
  resolution_time_avg: number; // in minutes
  active_incidents: number;
}

const mockEvents: TimelineEvent[] = [
  {
    id: 'tl-001',
    timestamp: '2026-05-22T10:45:00Z',
    type: 'threat_detected',
    severity: 'critical',
    title: 'Advanced Persistent Threat Detected',
    description: 'Suspicious lateral movement patterns detected across multiple endpoints',
    source: 'CrowdStrike EDR',
    affected_assets: ['LAPTOP-DEV-003', 'SERVER-PROD-01', 'DB-PRIMARY'],
    duration: 25,
    status: 'investigating',
    tags: ['apt', 'lateral-movement', 'multi-stage'],
    location: 'Production Network',
    impact_score: 9
  },
  {
    id: 'tl-002',
    timestamp: '2026-05-22T09:30:00Z',
    type: 'vulnerability_found',
    severity: 'high',
    title: 'Critical SQL Injection Vulnerability',
    description: 'Automated scan discovered SQL injection in user authentication endpoint',
    source: 'OWASP ZAP',
    affected_assets: ['api.company.com', 'user-auth-service'],
    status: 'mitigated',
    tags: ['sql-injection', 'authentication', 'web-app'],
    impact_score: 7
  },
  {
    id: 'tl-003',
    timestamp: '2026-05-22T08:15:00Z',
    type: 'incident_resolved',
    severity: 'high',
    title: 'Malware Incident Resolved',
    description: 'Trojan.Win32.GenKryptik successfully isolated and removed from endpoint',
    source: 'Security Operations',
    affected_assets: ['LAPTOP-DEV-003'],
    duration: 180,
    status: 'resolved',
    tags: ['malware', 'trojan', 'endpoint'],
    user: 'john.smith@company.com',
    impact_score: 6
  },
  {
    id: 'tl-004',
    timestamp: '2026-05-22T07:45:00Z',
    type: 'access_anomaly',
    severity: 'medium',
    title: 'Unusual Login Activity',
    description: 'Multiple failed login attempts followed by successful login from new location',
    source: 'Azure AD',
    affected_assets: ['admin@company.com'],
    status: 'investigating',
    tags: ['anomalous-login', 'geolocation', 'admin-access'],
    location: 'Tokyo, Japan',
    user: 'admin@company.com',
    impact_score: 5
  },
  {
    id: 'tl-005',
    timestamp: '2026-05-22T06:30:00Z',
    type: 'policy_violation',
    severity: 'medium',
    title: 'Data Classification Policy Violation',
    description: 'Sensitive data uploaded to unauthorized cloud storage service',
    source: 'DLP Scanner',
    affected_assets: ['employee-records.xlsx', 'dropbox.com'],
    status: 'mitigated',
    tags: ['data-loss-prevention', 'cloud-storage', 'sensitive-data'],
    user: 'hr.team@company.com',
    impact_score: 4
  },
  {
    id: 'tl-006',
    timestamp: '2026-05-22T05:00:00Z',
    type: 'compliance_check',
    severity: 'info',
    title: 'SOC 2 Control Assessment Completed',
    description: 'Automated compliance scan completed for CC6.6 encryption requirements',
    source: 'Compliance Engine',
    affected_assets: ['production-databases', 'data-stores'],
    status: 'resolved',
    tags: ['soc2', 'encryption', 'compliance'],
    impact_score: 2
  },
  {
    id: 'tl-007',
    timestamp: '2026-05-22T03:20:00Z',
    type: 'threat_detected',
    severity: 'low',
    title: 'Suspicious Network Scan',
    description: 'Port scanning activity detected from external IP address',
    source: 'Network IDS',
    affected_assets: ['firewall-01', 'dmz-subnet'],
    status: 'resolved',
    tags: ['port-scan', 'reconnaissance', 'external'],
    location: 'DMZ Network',
    impact_score: 3
  }
];

const mockMetrics: TimelineMetrics = {
  period: '24h',
  total_events: 247,
  events_by_hour: [12, 8, 6, 14, 9, 18, 15, 22, 28, 35, 31, 29, 42, 38, 33, 27, 24, 19, 16, 21, 18, 15, 13, 11],
  events_by_severity: {
    critical: 3,
    high: 12,
    medium: 45,
    low: 78,
    info: 109
  },
  events_by_type: {
    threat_detected: 45,
    vulnerability_found: 23,
    incident_resolved: 18,
    policy_violation: 67,
    access_anomaly: 34,
    compliance_check: 60
  },
  resolution_time_avg: 127,
  active_incidents: 8
};

const severityTone: Record<string, ToneType> = {
  critical: 'crit',
  high: 'warn',
  medium: 'info',
  low: 'muted',
  info: 'muted'
};

const statusTone: Record<string, ToneType> = {
  ongoing: 'crit',
  investigating: 'warn',
  mitigated: 'info',
  resolved: 'ok'
};

const typeIcons: Record<string, string> = {
  threat_detected: 'shield',
  vulnerability_found: 'alert-triangle',
  incident_resolved: 'check',
  policy_violation: 'settings',
  access_anomaly: 'user',
  compliance_check: 'book'
};

const typeColors: Record<string, string> = {
  threat_detected: '#F40B0B',
  vulnerability_found: '#CA9401',
  incident_resolved: '#159D4C',
  policy_violation: '#7F56D9',
  access_anomaly: '#0A69DA',
  compliance_check: '#6B7280'
};

function formatTime(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false 
  });
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

function getImpactColor(score: number): string {
  if (score >= 8) return '#F40B0B';
  if (score >= 6) return '#CA9401';
  if (score >= 4) return '#0A69DA';
  return '#6B7280';
}

export default function SecurityTimeline() {
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [metrics, setMetrics] = useState<TimelineMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('all');
  const [search, setSearch] = useState<string>('');
  const [timeRange, setTimeRange] = useState<string>('24h');

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 500));
      setEvents(mockEvents);
      setMetrics(mockMetrics);
      setLoading(false);
    };
    
    loadData();
  }, [timeRange]);

  const filteredEvents = events.filter(event => {
    const matchesFilter = filter === 'all' || 
      event.severity === filter ||
      event.type === filter ||
      event.status === filter;
    
    const matchesSearch = search === '' || 
      event.title.toLowerCase().includes(search.toLowerCase()) ||
      event.description.toLowerCase().includes(search.toLowerCase()) ||
      event.source.toLowerCase().includes(search.toLowerCase()) ||
      event.tags.some(tag => tag.toLowerCase().includes(search.toLowerCase()));
    
    return matchesFilter && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex items-center gap-2 text-ink/60">
          <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
          </svg>
          <span className="font-display text-[16px]">Loading security timeline...</span>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Security Timeline"
        subtitle="Chronological view of security events, incidents, and trends across your infrastructure."
        right={
          <div className="flex items-center gap-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="font-display text-[14px] text-ink border border-[#F3E4C8] rounded-[6px] px-3 py-2 bg-white"
            >
              <option value="1h">Last Hour</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
            </select>
            <button className="h-10 px-4 rounded-[10px] border border-[#F3E4C8] bg-white font-display font-bold text-[14px] inline-flex items-center gap-2 hover:border-ink/40">
              <Icon name="download" size={14} stroke="#121111" /> Export
            </button>
            <button className="h-10 px-4 rounded-[10px] bg-[#FA500E] text-white font-display font-bold text-[14px] inline-flex items-center gap-2 hover:opacity-90">
              <Icon name="refresh-cw" size={14} stroke="white" /> Refresh
            </button>
          </div>
        }
      />

      {/* Metrics Overview */}
      <div className="grid grid-cols-4 gap-4">
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Total Events</div>
          <div className="font-display font-bold text-ink mt-0.5 text-[24px]">{metrics.total_events}</div>
        </div>
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Active Incidents</div>
          <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#F40B0B' }}>{metrics.active_incidents}</div>
        </div>
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Avg Resolution</div>
          <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#0A69DA' }}>{formatDuration(metrics.resolution_time_avg)}</div>
        </div>
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-5">
          <div className="font-display text-[11px] font-bold uppercase tracking-[0.04em] text-ink/55">Critical Events</div>
          <div className="font-display font-bold mt-0.5 text-[24px]" style={{ color: '#F40B0B' }}>{metrics.events_by_severity.critical}</div>
        </div>
      </div>

      {/* Event Trend Chart */}
      <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-ink text-[18px]">Event Frequency ({timeRange})</h3>
          <div className="flex items-center gap-4">
            <div className="grid grid-cols-2 gap-4 text-[12px] text-ink/60">
              <div>Peak: {Math.max(...metrics.events_by_hour)} events</div>
              <div>Average: {Math.round(metrics.events_by_hour.reduce((a, b) => a + b, 0) / metrics.events_by_hour.length)} events/hour</div>
            </div>
          </div>
        </div>
        <div className="flex items-end justify-center">
          <Sparkline 
            data={metrics.events_by_hour} 
            width={800} 
            height={120} 
            color="#FA500E" 
            fill={true} 
          />
        </div>
      </div>

      {/* Severity Distribution */}
      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-6">
          <h3 className="font-display font-bold text-ink text-[18px] mb-4">Events by Severity</h3>
          <div className="space-y-3">
            {Object.entries(metrics.events_by_severity).map(([severity, count]) => {
              const total = Object.values(metrics.events_by_severity).reduce((a, b) => a + b, 0);
              const percentage = Math.round((count / total) * 100);
              return (
                <div key={severity} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge tone={severityTone[severity]} dot className="capitalize">
                      {severity}
                    </Badge>
                    <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                          width: `${percentage}%`,
                          background: severityTone[severity] === 'crit' ? '#F40B0B' : 
                                     severityTone[severity] === 'warn' ? '#CA9401' :
                                     severityTone[severity] === 'info' ? '#0A69DA' : '#6B7280'
                        }}
                      />
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-bold text-ink text-[14px]">{count}</div>
                    <div className="font-display text-[11px] text-ink/50">{percentage}%</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-[10px] border border-[#F3E4C8] bg-white p-6">
          <h3 className="font-display font-bold text-ink text-[18px] mb-4">Events by Type</h3>
          <div className="space-y-2">
            {Object.entries(metrics.events_by_type).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon 
                    name={typeIcons[type] as any} 
                    size={14} 
                    stroke={typeColors[type]} 
                  />
                  <span className="font-display text-[13px] text-ink capitalize">
                    {type.replace('_', ' ')}
                  </span>
                </div>
                <span className="font-display font-bold text-ink text-[14px]">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Icon name="search" size={16} className="text-ink/40" />
          <input
            type="text"
            placeholder="Search timeline events..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="font-display text-[14px] text-ink placeholder-ink/40 border-none outline-none bg-transparent"
          />
        </div>
        
        <div className="flex items-center gap-2 border-l border-gray-200 pl-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="font-display text-[14px] text-ink border border-[#F3E4C8] rounded-[6px] px-3 py-2 bg-white"
          >
            <option value="all">All Events</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="ongoing">Ongoing</option>
            <option value="investigating">Investigating</option>
            <option value="threat_detected">Threats</option>
            <option value="vulnerability_found">Vulnerabilities</option>
          </select>
        </div>
      </div>

      {/* Timeline Events */}
      <div className="rounded-[10px] border border-[#F3E4C8] bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-[#F7EDDB] bg-[#FCF8F1] font-display font-bold text-[11px] uppercase tracking-[0.1em] text-ink/60">
          Security Timeline ({filteredEvents.length} events)
        </div>
        
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-[29px] top-0 bottom-0 w-px bg-[#F7EDDB]" />
          
          <ul>
            {filteredEvents.map((event, index) => (
              <li key={event.id} className="relative flex gap-4 px-5 py-4 hover:bg-[#FCF8F1] border-b border-[#F7EDDB] last:border-0">
                {/* Timeline marker */}
                <div className="relative z-10 flex-shrink-0">
                  <div className="flex flex-col items-center">
                    <div 
                      className="w-8 h-8 rounded-full grid place-items-center border-2 border-white"
                      style={{ background: typeColors[event.type] }}
                    >
                      <Icon 
                        name={typeIcons[event.type] as any} 
                        size={14} 
                        stroke="white" 
                      />
                    </div>
                    <div className="font-data text-[10px] text-ink/50 mt-1">
                      {formatTime(event.timestamp)}
                    </div>
                  </div>
                </div>

                <div className="flex-1 min-w-0 pt-0.5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-display font-bold text-ink text-[16px]">{event.title}</h3>
                        <Badge tone={severityTone[event.severity]} dot className="capitalize">
                          {event.severity}
                        </Badge>
                        <Badge tone={statusTone[event.status]} className="capitalize">
                          {event.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      
                      <p className="font-display text-[14px] text-ink/65 mb-3 line-clamp-2">
                        {event.description}
                      </p>
                      
                      <div className="grid grid-cols-2 gap-4 text-[12px] text-ink/50 mb-3">
                        <div><strong>Source:</strong> {event.source}</div>
                        <div><strong>Impact Score:</strong> 
                          <span 
                            className="font-bold ml-1"
                            style={{ color: getImpactColor(event.impact_score) }}
                          >
                            {event.impact_score}/10
                          </span>
                        </div>
                        {event.duration && (
                          <div><strong>Duration:</strong> {formatDuration(event.duration)}</div>
                        )}
                        {event.location && (
                          <div><strong>Location:</strong> {event.location}</div>
                        )}
                        {event.user && (
                          <div><strong>User:</strong> {event.user}</div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 mb-2">
                        {event.affected_assets.map(asset => (
                          <span key={asset} className="inline-flex items-center px-2 py-1 rounded-[4px] bg-[#F7EDDB] text-[#CA9401] font-data text-[10px] font-semibold">
                            {asset}
                          </span>
                        ))}
                      </div>

                      <div className="flex flex-wrap gap-1">
                        {event.tags.map(tag => (
                          <span key={tag} className="inline-flex items-center px-2 py-1 rounded-[4px] bg-[#EDE5FC] text-[#7F56D9] font-display text-[10px] font-semibold">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button className="h-8 px-3 rounded-[6px] border border-[#F3E4C8] bg-white font-display font-bold text-[12px] text-ink hover:border-ink/40">
                        Details
                      </button>
                      {event.status === 'ongoing' && (
                        <button className="h-8 px-3 rounded-[6px] bg-[#0A69DA] text-white font-display font-bold text-[12px] hover:opacity-90">
                          Investigate
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}